import { faCog, faEdit, faPauseCircle, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import usePresentations from '../../../../../../../hooks/usePresentations';
import type { Meetingroom, Slot } from '../../../../../../../interfaces/interfaces';
import { RootState } from '../../../../../../../reduxToolkit/store';
import { createDisplayLink } from '../../../../../../../services/helper';
import { PresentationGuestList } from '../../../../../../account/Booth/BoothPresentations/PresentationGuestList';
import PresentationInfosButton from '../../../../../../account/Booth/BoothPresentations/PresentationInfosButton';
import { MeetingDetailsButton } from '../../../../../../account/meetingrooms/MeetingDetailsButton';
import VyAnimatedFontAwesomeIconButton from '../../../../../../common/VyAnimatedFontAwesomeIconButton';
import VyTooltip from '../../../../../../common/VyTooltip';
import PresentationTypeWithTooltip from '../PresentationTypeWithToopltip';
import { PresentationLivestreamSettingsModal } from './PresentationLivestreamSettingsModal';

interface PresentationCardInterface {
  slot: Slot;
  isUnassigned?: boolean;
  onRemove?: () => void;
  onRefresh: () => void;
  onEditPresentation: (meetingroom: Meetingroom) => void;
}

const SinglePresentationCard = ({ slot, isUnassigned = false, onRemove, onRefresh, onEditPresentation }: PresentationCardInterface) => {
  const [showStreamEditModal, setShowStreamEditModal] = useState(false);
  const event = useSelector((state: RootState) => state.common.event);
  const eventId = event?._id;
  const { t } = useTranslation();
  const { remindHost, removeSlot, deletePresentation, getPlaceholderBackgroundImage } = usePresentations();

  const isLivestream = slot.meetingroom?.isStudioForPresentationSlot === true;

  return (
    <>
      <PresentationLivestreamSettingsModal
        studio={slot.meetingroom}
        onClose={() => setShowStreamEditModal(false)}
        open={showStreamEditModal}
        onUpdate={onRefresh}
      />
      <div
        className="relative flex justify-between p-5 duration-200 card-wrapper hover:shadow-2xl"
        id={slot?.meetingroom?._id}
        style={{
          backgroundImage: `linear-gradient(20deg, rgba(0, 0, 0, 0.4) 0%, rgba(150, 150, 150, 0.3) 60%, rgba(238, 238, 238, 0.2) 100%), url(${
            slot.meetingroom.presentationBackground || slot.meetingroom.booth?.backgroundImage
              ? createDisplayLink(slot.meetingroom.presentationBackground || slot.meetingroom.booth?.backgroundImage, false)
              : getPlaceholderBackgroundImage(slot.meetingroom)
          })`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}>
        <div className="flex flex-col justify-end w-full left-part">
          {slot?.meetingroom?.image ? (
            <div
              className="w-16 h-16 mb-1 border border-white rounded-full"
              style={{
                backgroundImage: `url(${createDisplayLink(slot.meetingroom.image, false)})`,
                backgroundPosition: 'center',
                backgroundSize: 'cover',
              }}
            />
          ) : (
            <></>
          )}
          <div className="mr-8 overflow-hidden">
            <p className="text-xs text-white text-opacity-75">{slot.meetingroom.name}</p>
            <span className="text-white">
              {slot.meetingroom.booth?.name} {slot.meetingroom.contactperson && `${slot.meetingroom.booth ? '| ' : ''} ${slot.meetingroom.contactperson}`}
            </span>
          </div>
        </div>
        <div className="absolute right-0 flex mr-4">
          <div>{isLivestream && <div className="p-1 text-xs font-bold text-white rounded bg-slate-800">Livestream</div>}</div>
          <div>
            <PresentationTypeWithTooltip slot={slot} className="p-1 ml-1 text-xs text-center text-white rounded cursor-default bg-slate-400" />
            {!slot.datetime_hostnotification_sent && !isUnassigned && !isLivestream && (
              <VyAnimatedFontAwesomeIconButton
                style={{ marginLeft: '0.5rem', marginRight: '0.5rem' }}
                tooltip={t('account:slots.slot-not-reminded') || ''}
                icon={faPauseCircle}
                color="white"
                onClick={() => remindHost(slot._id, eventId)}
                hideIconAfterPromise
              />
            )}

            <VyAnimatedFontAwesomeIconButton
              key={slot.meetingroom._id}
              style={{ marginLeft: '0.5rem', marginRight: '0.5rem' }}
              tooltip={isUnassigned ? t('account:presentation.delete') : t('account:slots.delete-slot')}
              icon={isUnassigned ? faTrash : faTimes}
              color="white"
              onClick={() => (isUnassigned ? deletePresentation(slot.meetingroom._id, onRemove) : removeSlot(slot._id, eventId, onRemove))}
            />
            <VyTooltip title={t('account:edit-presentation') || ''}>
              <FontAwesomeIcon
                style={{ color: 'white', cursor: 'pointer', marginLeft: '0.5rem', marginRight: '0.5rem' }}
                icon={faEdit}
                className="cursor-pointer"
                onClick={() => slot.meetingroom && onEditPresentation(slot.meetingroom)}
              />
            </VyTooltip>

            {!isLivestream && <PresentationInfosButton meetingroom={slot.meetingroom} useAnimatedIconBtn />}
            {!isLivestream && <MeetingDetailsButton meeting={slot.meetingroom} />}
            {isLivestream && !isUnassigned && (
              <FontAwesomeIcon
                style={{ color: 'white', cursor: 'pointer', marginLeft: '0.5rem', marginRight: '0.5rem' }}
                icon={faCog}
                className="cursor-pointer"
                onClick={() => slot.meetingroom && setShowStreamEditModal(true)}
              />
            )}
            {!isUnassigned && !isLivestream && <PresentationGuestList slot={slot} />}
          </div>

          {/*!isUnassigned && <span className="absolute bottom-0 right-0 mb-4 mr-4 text-xs text-opacity-25 text-slate-100">{datetime_start}</span>*/}
        </div>
      </div>
      <style jsx>{`
        .card-wrapper {
          width: 330px;
          min-height: 160px;
          font-family: 'Gilroy';
        }
      `}</style>
    </>
  );
};

export default SinglePresentationCard;
